import { Platform } from 'react-native';
import { appApiRoutes } from '@riskbase/types/http/appApi';
import { isError } from 'lib/isError';
import { randomKey } from 'lib/riskbase';
import { requestRoute } from 'service/api';
import { Api } from 'service/api/constants';
import { FILE_TYPES } from 'service/file/types';
import {
  ALERT_ERROR,
  doFileShareDirect,
  HIDE_BLOCKING_LOADER,
  NAVIGATE_MODAL,
  SHOW_BLOCKING_LOADER,
  doToast,
} from 'store/actions';
import { ModalScreens } from 'components/Navigation/types';
import {
  put,
  race,
  select,
  take,
  takeLatest,
  takeLeading,
} from 'typed-redux-saga';
import {
  doPropertyAnalysisFilterOptionsLoad,
  doPropertyAnalysisFilterOptionsLoadFailure,
  doPropertyAnalysisFilterOptionsLoadSuccess,
  doPropertyAnalysisFilterOpenModal,
  doPropertyAnalysisDatasetDecisionUpdate,
  doPropertyAnalysisDatasetDecisionUpdateFailure,
  doPropertyAnalysisDatasetDecisionUpdateSuccess,
  doPropertyAnalysisDerivedFieldUpdate,
  doPropertyAnalysisDerivedFieldUpdateFailure,
  doPropertyAnalysisDerivedFieldUpdateSuccess,
  doPropertyAnalysisExport,
  doPropertyAnalysisExportFailure,
  doPropertyAnalysisExportSuccess,
  doPropertyAnalysisLoad,
  doPropertyAnalysisLoadFailure,
  doPropertyAnalysisLoadSuccess,
} from './actions';
import {
  selectPropertyAnalysisFilter,
  selectPropertyAnalysisFilterOptionsLoadStatus,
} from './selectors';

function* loadPropertyAnalysis(
  action: ReturnType<typeof doPropertyAnalysisLoad>,
): Generator {
  const page = action.payload.page ?? 1;
  const currentFilter = yield* select(selectPropertyAnalysisFilter);
  const filter = { ...currentFilter, ...action.payload.filter };

  try {
    const response = yield* requestRoute({
      api: Api.APP,
      route: appApiRoutes.propertyAnalysis.listCssProperties,
      query: {
        page,
        limit: 50,
        search: filter.search.trim() || undefined,
        priorities: filter.priorities.length ? filter.priorities : undefined,
        datasetDecisions: filter.datasetDecisions.length
          ? filter.datasetDecisions
          : undefined,
        hasConflicts: filter.hasConflicts,
        heightCategories: filter.heightCategories.length
          ? filter.heightCategories
          : undefined,
        buildingAgeBands: filter.buildingAgeBands.length
          ? filter.buildingAgeBands
          : undefined,
        populationDensityBands: filter.populationDensityBands.length
          ? filter.populationDensityBands
          : undefined,
      },
    });

    yield* put(
      doPropertyAnalysisLoadSuccess({
        items: response.data,
        summary: response.summary,
        pagination: response.pagination,
        append: page > 1,
      }),
    );
  } catch (error) {
    yield* put(
      doPropertyAnalysisLoadFailure({
        error: isError(error)
          ? error.message
          : 'Unable to load property analysis.',
        page,
      }),
    );
  }
}

function* loadPropertyAnalysisFilterOptions(): Generator {
  try {
    const filterOptions = yield* requestRoute({
      api: Api.APP,
      route: appApiRoutes.propertyAnalysis.getCssFilterOptions,
    });

    yield* put(doPropertyAnalysisFilterOptionsLoadSuccess({ filterOptions }));
  } catch (error) {
    yield* put(
      doPropertyAnalysisFilterOptionsLoadFailure({
        error: isError(error)
          ? error.message
          : 'Unable to load property analysis filters.',
      }),
    );
  }
}

function* exportPropertyAnalysis(): Generator {
  const filter = yield* select(selectPropertyAnalysisFilter);

  const blockingLoaderKey = randomKey();
  yield* put(SHOW_BLOCKING_LOADER(blockingLoaderKey));

  try {
    const response = yield* requestRoute({
      api: Api.APP,
      route: appApiRoutes.propertyAnalysis.exportCssProperties,
      query: {
        search: filter.search.trim() || undefined,
        priorities: filter.priorities.length ? filter.priorities : undefined,
        hasConflicts: filter.hasConflicts,
        heightCategories: filter.heightCategories.length
          ? filter.heightCategories
          : undefined,
        buildingAgeBands: filter.buildingAgeBands.length
          ? filter.buildingAgeBands
          : undefined,
        populationDensityBands: filter.populationDensityBands.length
          ? filter.populationDensityBands
          : undefined,
      },
    });

    if (Platform.OS === 'web' && typeof window !== 'undefined') {
      window.location.href = response.url;
    } else {
      yield* put(
        doFileShareDirect({
          url: response.url,
          title: response.fileName,
          fileType: FILE_TYPES.csv,
        }),
      );
    }
    yield* put(doPropertyAnalysisExportSuccess());
  } catch (error) {
    const message = isError(error)
      ? error.message
      : 'Unable to export property analysis.';
    yield* put(doPropertyAnalysisExportFailure({ error: message }));
    yield* put(ALERT_ERROR({ error: new Error(message) }));
  } finally {
    yield* put(HIDE_BLOCKING_LOADER(blockingLoaderKey));
  }
}

function* openPropertyAnalysisFilterModal(): Generator {
  const filterOptionsLoadStatus = yield* select(
    selectPropertyAnalysisFilterOptionsLoadStatus,
  );

  if (filterOptionsLoadStatus !== 'loaded') {
    yield* put(SHOW_BLOCKING_LOADER());
    if (filterOptionsLoadStatus !== 'loading') {
      yield* put(doPropertyAnalysisFilterOptionsLoad());
    }

    const taken = yield* race({
      fail: take(doPropertyAnalysisFilterOptionsLoadFailure),
      done: take(doPropertyAnalysisFilterOptionsLoadSuccess),
    });

    yield* put(HIDE_BLOCKING_LOADER());

    if (taken.fail) {
      yield* put(ALERT_ERROR({ error: new Error(taken.fail.payload.error) }));
      return;
    }
  }

  yield* put(
    NAVIGATE_MODAL({
      screen: ModalScreens.PropertyAnalysisFilter,
      size: 'small',
      params: {},
    }),
  );
}

function* updatePropertyAnalysisDatasetDecision(
  action: ReturnType<typeof doPropertyAnalysisDatasetDecisionUpdate>,
): Generator {
  const { propertyId, decision } = action.payload;

  try {
    const response = yield* requestRoute({
      api: Api.APP,
      route: appApiRoutes.propertyAnalysis.updatePropertyField,
      params: { propertyId },
      body: { field: 'datasetDecision', value: decision },
    });

    yield* put(
      doPropertyAnalysisDatasetDecisionUpdateSuccess({
        property: response.data,
      }),
    );
    yield* put(doToast('Dataset decision updated.'));
  } catch (error) {
    const message = isError(error)
      ? error.message
      : 'Unable to update the dataset decision.';
    yield* put(
      doPropertyAnalysisDatasetDecisionUpdateFailure({
        propertyId,
        error: message,
      }),
    );
    yield* put(ALERT_ERROR({ error: new Error(message) }));
  }
}

function* updatePropertyAnalysisDerivedField(
  action: ReturnType<typeof doPropertyAnalysisDerivedFieldUpdate>,
): Generator {
  const { propertyId, field, value } = action.payload;

  try {
    const response = yield* requestRoute({
      api: Api.APP,
      route: appApiRoutes.propertyAnalysis.updatePropertyField,
      params: { propertyId },
      body: { field, value },
    });

    yield* put(
      doPropertyAnalysisDerivedFieldUpdateSuccess({
        property: response.data,
      }),
    );
    yield* put(doToast('Property analysis value updated.'));
  } catch (error) {
    const message = isError(error)
      ? error.message
      : 'Unable to update the property analysis value.';
    yield* put(
      doPropertyAnalysisDerivedFieldUpdateFailure({
        propertyId,
        error: message,
      }),
    );
    yield* put(ALERT_ERROR({ error: new Error(message) }));
  }
}

export const propertyAnalysisSideEffects = [
  takeLatest(doPropertyAnalysisLoad, loadPropertyAnalysis),
  takeLeading(doPropertyAnalysisExport, exportPropertyAnalysis),
  takeLatest(
    doPropertyAnalysisFilterOptionsLoad,
    loadPropertyAnalysisFilterOptions,
  ),
  takeLeading(
    doPropertyAnalysisFilterOpenModal,
    openPropertyAnalysisFilterModal,
  ),
  takeLeading(
    doPropertyAnalysisDatasetDecisionUpdate,
    updatePropertyAnalysisDatasetDecision,
  ),
  takeLeading(
    doPropertyAnalysisDerivedFieldUpdate,
    updatePropertyAnalysisDerivedField,
  ),
];
